import { lazy, Suspense, useState, type FC } from 'react'
import classNames from 'classnames'

import { Text } from 'components'

import styles from './Licenses.module.scss'

const LicenseViewer = lazy(() => import('../LicenseViewer'))

type TActive = { file: string; titleKey: string; code: string }

type TLicense = {
  title: string
  code: string
  file: string
}

type TGroup = {
  activity: string
  code: string
  items: TLicense[]
}

const base = `${process.env.PUBLIC_URL || ''}/licenses`

const groups: TGroup[] = [
  {
    activity: 'licDesign',
    code: 'ՔՊԼ-002752',
    items: [
      { title: 'licCatUrbanDocs', code: 'ՔՊԼ-002752', file: `${base}/QPL-002752-design-urban-documents.pdf` },
      { title: 'licCatElectricity', code: 'ՔՊԼ-002752-05', file: `${base}/QPL-002752-05-electricity.pdf` },
      { title: 'licCatHeating', code: 'ՔՊՆ-002752-06', file: `${base}/QPN-002752-06-heating-ventilation.pdf` },
      { title: 'licCatWater', code: 'ՔՊՆ-002752-08', file: `${base}/QPN-002752-08-water-drainage.pdf` },
      { title: 'licCatCommunication', code: 'ՔՊՆ-002752-10', file: `${base}/QPN-002752-10-communication.pdf` },
    ],
  },
  {
    activity: 'licConstruction',
    code: 'ՔՊԼ-002992',
    items: [
      { title: 'licCatConstructionWorks', code: 'ՔՊԼ-002992', file: `${base}/QPL-002992-construction.pdf` },
      { title: 'licCatElectricity', code: 'ՔՊԼ-002992-05', file: `${base}/QPL-002992-05-electricity.pdf` },
      { title: 'licCatHeating', code: 'ՔՊԼ-002992-06', file: `${base}/QPL-002992-06-heating-ventilation.pdf` },
      { title: 'licCatWater', code: 'ՔՊԼ-002992-08', file: `${base}/QPL-002992-08-water-drainage.pdf` },
      { title: 'licCatCommunication', code: 'ՔՊԼ-002992-10', file: `${base}/QPL-002992-10-communication.pdf` },
    ],
  },
]

const DocumentIcon: FC = () => (
  <svg viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg' className={styles.card__icon}>
    <path
      d='M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinejoin='round'
    />
    <path d='M14 3v5h5M8.5 13h7M8.5 16.5h7' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
  </svg>
)

const Licenses: FC = () => {
  const [active, setActive] = useState<TActive | null>(null)

  return (
    <section className={styles.licenses}>
      <div className={classNames('container', styles.licenses__inner)}>
        <div className={styles.licenses__head}>
          <span className={styles.licenses__accent} aria-hidden />
          <Text text='licensesTitle' className={styles.licenses__title} tagName='h2' />
          <Text text='licensesSubtitle' className={styles.licenses__subtitle} />
        </div>

        {groups.map(group => (
          <div className={styles.group} key={group.code}>
            <div className={styles.group__head}>
              <Text text={group.activity} className={styles.group__activity} tagName='h3' />
              <span className={styles.group__meta}>
                {group.code} · <Text text='firstClass' emptyTag />
              </span>
            </div>

            <div className={styles.grid}>
              {group.items.map(item => (
                <button
                  type='button'
                  key={item.code}
                  className={styles.card}
                  onClick={() => setActive({ file: item.file, titleKey: item.title, code: item.code })}
                >
                  <span className={styles.card__iconWrap} aria-hidden>
                    <DocumentIcon />
                  </span>
                  <span className={styles.card__body}>
                    <Text text={item.title} className={styles.card__title} />
                    <span className={styles.card__code}>{item.code}</span>
                  </span>
                  <span className={styles.card__view}>
                    <Text text='viewPdf' />
                    <span className={styles.card__view_icon} aria-hidden />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {active && (
        <Suspense fallback={null}>
          <LicenseViewer file={active.file} titleKey={active.titleKey} code={active.code} onClose={() => setActive(null)} />
        </Suspense>
      )}
    </section>
  )
}

export default Licenses
